import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  Textarea,
} from "@aman/design-system";
import { type FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, asApiError, type ApiError } from "../api/client";
import { ApiErrorAlert, PageHeader } from "../components/common";

const AVAILABLE_SCOPES = [
  "entitlements.read",
  "licenses.read",
  "operations.read",
  "operations.write",
  "operations.transition",
  "credits.read",
  "credits.reserve",
  "credits.capture",
  "credits.release",
  "resources.read",
  "artifacts.request_access",
  "notifications.send",
  "events.publish",
  "streams.manage",
  "updates.read",
  "plugin_users.read",
  "plugin_users.write",
  "plugin_users.authenticate",
  "balances.read",
  "balances.reserve",
  "balances.capture",
  "balances.release",
  "balances.credit",
  "balances.debit",
];

interface BuilderState {
  id: string;
  name: string;
  version: string;
  description: string;
  author: string;
  coreMinimum: string;
  coreMaximum: string;
  image: string;
  apiPort: string;
  healthPath: string;
  entry: string;
  internet: boolean;
  adminUi: boolean;
  scopes: string[];
  productKey: string;
  productName: string;
  planKey: string;
  planName: string;
  planDurationDays: string;
  planCredits: string;
  offlineLeaseHours: string;
  validationMinutes: string;
}

const initial: BuilderState = {
  id: "",
  name: "",
  version: "1.0.0",
  description: "",
  author: "",
  coreMinimum: "0.1.0",
  coreMaximum: "1.0.0",
  image: "",
  apiPort: "8080",
  healthPath: "/health",
  entry: "/ui/index.html",
  internet: false,
  adminUi: true,
  scopes: ["plugin_users.read", "plugin_users.write", "balances.read"],
  productKey: "",
  productName: "",
  planKey: "annual",
  planName: "Annual",
  planDurationDays: "365",
  planCredits: "0",
  offlineLeaseHours: "24",
  validationMinutes: "15",
};

export function PluginBuilderPage() {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [state, setState] = useState<BuilderState>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError>();
  const [copied, setCopied] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const manifest = useMemo(
    () => ({
      apiVersion: "aman.plugins/v2",
      kind: "Plugin",
      id: state.id,
      name: state.name,
      version: state.version,
      description: state.description,
      author: { name: state.author },
      coreCompatibility: {
        minimum: state.coreMinimum,
        maximum: state.coreMaximum,
      },
      runtime: {
        image: state.image || `aman-plugin-${state.id}:${state.version}`,
        apiPort: Number(state.apiPort),
        healthPath: state.healthPath,
        readinessPath: state.healthPath,
        resources: { cpu: 0.5, memoryMb: 256, pidsLimit: 128 },
      },
      components: { api: true, worker: false, scheduler: false },
      database: { migrations: true, migrationsPath: "migrations" },
      permissions: { core: state.scopes },
      network: { internet: state.internet, allowedDomains: [] },
      ui: {
        admin: state.adminUi,
        reseller: state.adminUi,
        customer: false,
        mode: "custom",
        entry: state.entry || "/ui/index.html",
      },
      events: { subscribe: [], publish: [] },
      routes: state.adminUi
        ? [
            {
              key: "home",
              i18nKey: "plugin.home",
              path: "/",
              icon: "plugin",
              audience: ["admin"],
              requiredScope: state.scopes[0] ?? "plugin_users.read",
            },
          ]
        : [],
      product: {
        key: state.productKey || state.id,
        name: state.productName || state.name,
        nameI18n: {},
        licensing: {
          validationIntervalSeconds: Number(state.validationMinutes) * 60,
          offlineLeaseSeconds: Number(state.offlineLeaseHours) * 3_600,
          offlineGraceSeconds: 0,
          offlineExpiryAction: "custom",
          fingerprintMatchThreshold: 3,
          selfTransferLimit: 2,
          selfTransferWindowDays: 365,
        },
        entitlementDefinitions: [],
        plans: [
          {
            key: state.planKey,
            name: state.planName,
            nameI18n: {},
            licenseType: "subscription",
            durationDays: Number(state.planDurationDays),
            maxDevices: 1,
            entitlements: {},
            prices: [{ currency: "credits", amount: Number(state.planCredits) }],
            availableForSale: true,
          },
        ],
        operations: [],
        settings: [],
      },
    }),
    [state],
  );
  const prompt = useMemo(
    () =>
      `You are building the Aman plugin “${state.name}” (${state.id}) version ${state.version}.\n\nDeliver exactly one installable file named ${state.id || "plugin-id"}.tar. The archive root must contain aman.plugin.json and may contain image.tar produced by docker save. Implement the manifest contract without changing its id. The service must listen on port ${state.apiPort}, expose ${state.healthPath}, run as a non-root user, keep its root filesystem read-only, and use only its injected plugin database. Never connect to the Aman Core database. Exchange AMAN_PLUGIN_BOOTSTRAP_SECRET for a scoped token, then use CORE_API_INTERNAL_URL for all Core operations. Use idempotency keys for every mutation. Requested scopes: ${state.scopes.join(", ") || "none"}. ${state.internet ? "Internet access is requested; restrict calls to declared domains." : "The plugin must work without internet access."} The admin UI entry is ${state.adminUi ? state.entry : "disabled"}; it runs in a sandboxed cross-origin iframe and receives {type:'aman:init', token, pluginId, locale, theme} through postMessage. Validate against the bundled mock Core and test vectors, run all tests, and complete CHECKLIST.md before packaging.`,
    [state],
  );
  const valid =
    /^[a-z][a-z0-9-]{1,38}[a-z0-9]$/u.test(state.id) &&
    state.name.trim().length >= 2 &&
    state.author.trim().length >= 2 &&
    state.description.trim().length >= 10 &&
    /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/u.test(state.version) &&
    /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/u.test(state.coreMinimum) &&
    /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/u.test(state.coreMaximum) &&
    Number(state.apiPort) >= 1024 &&
    Number(state.apiPort) <= 65535 &&
    /^[a-z0-9-]{2,64}$/u.test(state.productKey || state.id) &&
    (state.productName || state.name).trim().length > 0 &&
    /^[a-z0-9-]{2,64}$/u.test(state.planKey) &&
    state.planName.trim().length > 0 &&
    Number.isInteger(Number(state.planDurationDays)) &&
    Number(state.planDurationDays) > 0 &&
    Number.isInteger(Number(state.planCredits)) &&
    Number(state.planCredits) >= 0 &&
    Number(state.offlineLeaseHours) > 0 &&
    Number(state.validationMinutes) >= 1;
  const update = <K extends keyof BuilderState>(
    key: K,
    value: BuilderState[K],
  ): void => setState((current) => ({ ...current, [key]: value }));
  const next = (event: FormEvent): void => {
    event.preventDefault();
    setValidationAttempted(true);
    if (valid) {
      setValidationAttempted(false);
      setStep(1);
    }
  };
  const download = async (format: "tar.gz" | "zip"): Promise<void> => {
    setBusy(true);
    setError(undefined);
    try {
      const result = await api.post<unknown>(
        `/plugins/build-kit${format === "zip" ? "?format=zip" : ""}`,
        { manifest, agentPrompt: prompt },
        { raw: true },
      );
      const blob =
        result instanceof Blob
          ? result
          : new Blob([JSON.stringify(result, null, 2)], {
              type: format === "zip" ? "application/zip" : "application/gzip",
            });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${state.id}-build-kit.${format}`;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (caught) {
      setError(asApiError(caught));
    } finally {
      setBusy(false);
    }
  };
  const copyPrompt = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
    } catch (caught) {
      setError(asApiError(caught));
    }
  };
  const steps = [
    t("pages.pluginBuilder.config"),
    t("pages.pluginBuilder.manifest"),
    t("pages.pluginBuilder.prompt"),
    t("pages.pluginBuilder.package"),
  ];
  return (
    <div className="page-stack">
      <PageHeader
        title={t("pages.pluginBuilder.title")}
        subtitle={t("pages.pluginBuilder.subtitle")}
      />
      <ApiErrorAlert error={error} />
      {validationAttempted && !valid ? (
        <Alert tone="warning" title={t("pages.pluginBuilder.validationTitle")}>
          {t("pages.pluginBuilder.validationMessage")}
        </Alert>
      ) : null}
      <ol className="wizard-steps">
        {steps.map((label, index) => (
          <li
            className={index === step ? "active" : index < step ? "done" : ""}
            key={label}
          >
            <span>{index < step ? "✓" : index + 1}</span>
            <strong>{label}</strong>
          </li>
        ))}
      </ol>
      {step === 0 ? (
        <Card>
          <form className="builder-form" onSubmit={next}>
            <div className="form-grid form-grid--two">
              <Field
                label={t("pages.pluginBuilder.pluginId")}
                hint="lowercase-kebab-case"
              >
                <Input
                  required
                  dir="ltr"
                  pattern="[a-z][a-z0-9-]{1,38}[a-z0-9]"
                  value={state.id}
                  onChange={(event) => update("id", event.target.value)}
                />
              </Field>
              <Field label={t("common.name")}>
                <Input
                  required
                  value={state.name}
                  onChange={(event) => update("name", event.target.value)}
                />
              </Field>
              <Field label={t("common.version")}>
                <Input
                  required
                  dir="ltr"
                  value={state.version}
                  onChange={(event) => update("version", event.target.value)}
                />
              </Field>
              <Field label={t("pages.pluginBuilder.author")}>
                <Input
                  required
                  value={state.author}
                  onChange={(event) => update("author", event.target.value)}
                />
              </Field>
              <Field label={t("pages.pluginBuilder.coreMin")}>
                <Input
                  dir="ltr"
                  value={state.coreMinimum}
                  onChange={(event) =>
                    update("coreMinimum", event.target.value)
                  }
                />
              </Field>
              <Field label="Maximum Core version">
                <Input
                  dir="ltr"
                  value={state.coreMaximum}
                  onChange={(event) =>
                    update("coreMaximum", event.target.value)
                  }
                />
              </Field>
              <Field label={t("pages.pluginBuilder.apiPort")}>
                <Input
                  type="number"
                  min="1024"
                  max="65535"
                  value={state.apiPort}
                  onChange={(event) => update("apiPort", event.target.value)}
                />
              </Field>
              <Field label="Health path">
                <Input
                  dir="ltr"
                  value={state.healthPath}
                  onChange={(event) => update("healthPath", event.target.value)}
                />
              </Field>
              <Field label="Container image">
                <Input
                  dir="ltr"
                  value={state.image}
                  placeholder={`aman-plugin-${state.id || "id"}:${state.version}`}
                  onChange={(event) => update("image", event.target.value)}
                />
              </Field>
              <Field label="UI entry">
                <Input
                  dir="ltr"
                  disabled={!state.adminUi}
                  value={state.entry}
                  onChange={(event) => update("entry", event.target.value)}
                />
              </Field>
            </div>
            <Field label={t("common.description")}>
              <Textarea
                required
                value={state.description}
                onChange={(event) => update("description", event.target.value)}
              />
            </Field>
            <h3>Product catalog</h3>
            <div className="form-grid form-grid--two">
              <Field label="Product key" hint="Created automatically when the plugin is installed">
                <Input
                  dir="ltr"
                  pattern="[a-z0-9-]{2,64}"
                  value={state.productKey}
                  placeholder={state.id || "licensed-product"}
                  onChange={(event) => update("productKey", event.target.value)}
                />
              </Field>
              <Field label="Product name">
                <Input
                  value={state.productName}
                  placeholder={state.name || "Licensed product"}
                  onChange={(event) => update("productName", event.target.value)}
                />
              </Field>
              <Field label="Initial plan key">
                <Input
                  required
                  dir="ltr"
                  pattern="[a-z0-9-]{2,64}"
                  value={state.planKey}
                  onChange={(event) => update("planKey", event.target.value)}
                />
              </Field>
              <Field label="Initial plan name">
                <Input
                  required
                  value={state.planName}
                  onChange={(event) => update("planName", event.target.value)}
                />
              </Field>
              <Field label="Plan duration (days)">
                <Input
                  required
                  type="number"
                  min="1"
                  step="1"
                  value={state.planDurationDays}
                  onChange={(event) => update("planDurationDays", event.target.value)}
                />
              </Field>
              <Field label="Plan price (credits)">
                <Input
                  required
                  type="number"
                  min="0"
                  step="1"
                  value={state.planCredits}
                  onChange={(event) => update("planCredits", event.target.value)}
                />
              </Field>
              <Field label="Offline lease (hours)">
                <Input
                  required
                  type="number"
                  min="1"
                  max="720"
                  step="1"
                  value={state.offlineLeaseHours}
                  onChange={(event) => update("offlineLeaseHours", event.target.value)}
                />
              </Field>
              <Field label="Validation interval (minutes)">
                <Input
                  required
                  type="number"
                  min="1"
                  max="1440"
                  step="1"
                  value={state.validationMinutes}
                  onChange={(event) => update("validationMinutes", event.target.value)}
                />
              </Field>
            </div>
            <div className="toggle-row">
              <label className="check-field">
                <input
                  type="checkbox"
                  checked={state.adminUi}
                  onChange={(event) => update("adminUi", event.target.checked)}
                />
                {t("pages.pluginBuilder.adminUi")}
              </label>
              <label className="check-field">
                <input
                  type="checkbox"
                  checked={state.internet}
                  onChange={(event) => update("internet", event.target.checked)}
                />
                {t("pages.pluginBuilder.internet")}
              </label>
            </div>
            <fieldset className="scope-picker">
              <legend>{t("pages.plugins.requestedScopes")}</legend>
              {AVAILABLE_SCOPES.map((scope) => (
                <label className="check-field" key={scope}>
                  <input
                    type="checkbox"
                    checked={state.scopes.includes(scope)}
                    onChange={(event) =>
                      update(
                        "scopes",
                        event.target.checked
                          ? [...state.scopes, scope]
                          : state.scopes.filter((item) => item !== scope),
                      )
                    }
                  />
                  <code>{scope}</code>
                </label>
              ))}
            </fieldset>
            <div className="form-actions">
              <Button type="submit">
                {t("pages.pluginBuilder.next")}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}
      {step === 1 ? (
        <Card title={t("pages.pluginBuilder.manifest")}>
          <pre className="manifest-preview" dir="ltr">
            {JSON.stringify(manifest, null, 2)}
          </pre>
          <div className="wizard-actions">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setStep(0)}
            >
              {t("common.back")}
            </Button>
            <Button type="button" onClick={() => setStep(2)}>
              {t("pages.pluginBuilder.next")}
            </Button>
          </div>
        </Card>
      ) : null}
      {step === 2 ? (
        <Card title={t("pages.pluginBuilder.prompt")}>
          <Textarea
            className="prompt-preview"
            dir="ltr"
            readOnly
            value={prompt}
          />
          <div className="wizard-actions">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setStep(1)}
            >
              {t("common.back")}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void copyPrompt()}
            >
              {copied
                ? t("common.copied")
                : t("pages.pluginBuilder.copyPrompt")}
            </Button>
            <Button type="button" onClick={() => setStep(3)}>
              {t("pages.pluginBuilder.next")}
            </Button>
          </div>
        </Card>
      ) : null}
      {step === 3 ? (
        <Card title={t("pages.pluginBuilder.package")}>
          <div className="package-summary">
            <span className="package-summary__icon">⇩</span>
            <div>
              <strong>{state.id}-build-kit</strong>
              <p>
                {state.name} · v{state.version} · {state.scopes.length} scopes
              </p>
            </div>
          </div>
          <Alert tone="info">
            The generated kit includes the manifest, integration contract,
            TypeScript/Python SDKs, example, mock Core, test vectors, agent
            prompt and checklist.
          </Alert>
          <div className="wizard-actions">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setStep(2)}
            >
              {t("common.back")}
            </Button>
            <Button
              type="button"
              variant="gold"
              busy={busy}
              onClick={() => void download("zip")}
            >
              {t("pages.pluginBuilder.downloadZip")}
            </Button>
            <Button
              type="button"
              busy={busy}
              onClick={() => void download("tar.gz")}
            >
              {t("pages.pluginBuilder.downloadTar")}
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
